import React, { useEffect, useRef, useState } from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SideNav from "component/layouts/sideNav";
import {
  setItemCategories,
  setItemList,
  setItems,
  setLabourerTypes,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setUoms,
  setUsers,
  siteCellRenderer,
  supplierCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";

import {
  ADD,
  DELETE,
  GO,
  LABOUR_MANAGEMENT,
  WORK_ORDER,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_LOGIN,
  PATH_WORK_ORDER,
  PATH_WORK_ORDER_DETAIL,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APILABOURMGMT } from "apihandler/labourmgmt";

import { setDates, setFilterModel } from "store/Slice/workOrdersSlice";
import { useDebounce } from "resources/constant/utilities/util";



export default function WorkOrder() {
  let { date } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gridRef = useRef();
  const workOrderCacheRef = useRef({});

  const getCacheKey = (from, to) =>
  `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;

  const { fromDate, toDate, filterModel } = useSelector((s) => s.workOrders);

  const [dateValue, setDateValue] = useState(null);
  const [toDateValue, setToDateValue] = useState(dayjs().endOf("month"));

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, []);

  const [selectedRowId,setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      console.log("delete the selected ids: ", selectedRowIds);
      APILABOURMGMT.DeleteWorkOrders({ selectedRowIds })
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText(
              "Success",
              "The records were deleted successfully"
            );
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const columnDefs = [
    {
      headerName: "Work Order No",
      field: "workOrderNumber",
      checkboxSelection: false,
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: "Contractor Name",
      field: "contractorId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) =>
        supplierCellRenderer({ value: params.data.contractorId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Effective Date",
      field: "effectiveDate",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: "Site",
      field: "locationId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) =>
        siteCellRenderer({ value: params.data.locationId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      valueGetter: (params) =>
        userCellRenderer({ value: params.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      flex: 0.4,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            aria-label="edit"
            onClick={() => {
              navigate(PATH_WORK_ORDER_DETAIL + params.data.id, {
                state: {
                  date: dayjs(dateValue).format("YYYY-MM-DD"),
                  isEdit: true,
                },
              });
            }}
          >
            <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
          <IconButton
            aria-label="view"
            onClick={() => {
              navigate(PATH_WORK_ORDER_DETAIL + params.data.id, {
                state: {
                  date: dayjs(dateValue).format("YYYY-MM-DD"),
                  isEdit: false,
                },
              });
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
  };

  const fetchWorkOrders = (d1, d2) => {
    return APILABOURMGMT.WorkOrders(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return resp.response.sort((a, b) => b.id - a.id);
      });
  };

  const primeLookups = () => {
    APIMASTER.SiteOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSites(r.response));
    APIMASTER.SupplierCategories()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSupplierCategories(r.response));
    APIMASTER.SupplierOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSuppliers(r.response));
    APIMASTER.LabourerTypeOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setLabourerTypes(r.response));
    APIMASTER.UomOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUoms(r.response));
    APIMASTER.UserOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUsers(r.response));
    APIMASTER.ItemCategories()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setItemCategories(r.response));
    APIMASTER.Items()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode !== 200) return;
        const itemOptions = r.response.map((e) => ({
          name: e.itemName,
          value: e.id,
          dependentvalue: e.categoryId,
        }));
        setItems(itemOptions);
        setItemList(r.response);
      });
  };

  const loadWorkOrdersWithCache = async (fromDate, toDate) => {
    try {
      setIsLoading(true);
      if (!gridRef.current?.api) return;

      const from = fromDate || dayjs().startOf("month");
      const to = toDate || dayjs().endOf("month");

      dispatch(
        setDates({
          fromDate: from.format("YYYY-MM-DD"),
          toDate: to.format("YYYY-MM-DD"),
        })
      );

      const cacheKey = getCacheKey(from, to);
      let data;

      if (workOrderCacheRef.current[cacheKey]) {
        data = workOrderCacheRef.current[cacheKey];
      } else {
        data = await fetchWorkOrders(from, to);
        workOrderCacheRef.current[cacheKey] = data;
      }

      try {
        gridRef.current.api.setRowData(data);
      } catch (e) {
        try {
          const rowData = [];
          gridRef.current.api.forEachNode((n) => rowData.push(n.data));
          if (rowData.length) {
            gridRef.current.api.applyTransaction({ remove: rowData });
          }
          gridRef.current.api.applyTransaction({ add: data });
        } catch (_) {}
      }

      if (filterModel) {
        gridRef.current.api.setFilterModel(filterModel);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedLoadWorkOrders = useDebounce(
  loadWorkOrdersWithCache,
  300
);

  const onGridReady = async (params) => {
    const d1 = date ? dayjs(date) : dayjs(fromDate || dayjs().startOf("month"));
    const d2 = date
      ? dayjs(toDateValue)
      : dayjs(toDate || dayjs().endOf("month"));

    setDateValue(d1);
    setToDateValue(d2);

    primeLookups();

    const data = await fetchWorkOrders(d1, d2);
    params.api.setRowData(data);

    if (filterModel) {
      params.api.setFilterModel(filterModel);
    }
  };

  const onFilterChanged = (event) => {
    const model = event.api.getFilterModel();
    dispatch(setFilterModel(model));
  };

  return (
    <SideNav
      currentComponent={LABOUR_MANAGEMENT}
      subComponent={WORK_ORDER}
      subComponentPath={PATH_WORK_ORDER}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_WORK_ORDER_DETAIL + 0, {
              state: {
                date: dayjs(dateValue).format("YYYY-MM-DD"),
                isEdit: false,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            disabled={isLoading}
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={() => {
              debouncedLoadWorkOrders(dateValue, toDateValue);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            onFilterChanged={onFilterChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}

